// LaTeX Source Panel — toggleable view of the raw LaTeX output

import { useState, useRef, useCallback, useEffect, type PointerEvent as ReactPointerEvent } from "react";
import { invoke } from "@tauri-apps/api/core";

interface LatexPanelProps {
    latex: string;
    latexFull: string;
    onImport?: (latex: string) => void;
}

// ── Highlighted code renderer ─────────────────────────────────────────────
// Splits `text` by all case-insensitive matches of `query` and returns
// an array of React elements with <mark> spans for matches.
function HighlightedCode({
    text,
    query,
    currentMatch,
}: {
    text: string;
    query: string;
    currentMatch: number; // 0-based index of the focused match
}) {
    if (!query) return <>{text}</>;

    const parts: React.ReactNode[] = [];
    const lower = text.toLowerCase();
    const lowerQ = query.toLowerCase();
    const len = lowerQ.length;
    let pos = 0;
    let matchIdx = 0;

    while (pos < text.length) {
        const idx = lower.indexOf(lowerQ, pos);
        if (idx === -1) {
            parts.push(text.slice(pos));
            break;
        }
        if (idx > pos) parts.push(text.slice(pos, idx));
        const isCurrent = matchIdx === currentMatch;
        parts.push(
            <mark
                key={`m-${matchIdx}`}
                className={`latex-match${isCurrent ? " latex-match--current" : ""}`}
                data-match-index={matchIdx}
            >
                {text.slice(idx, idx + len)}
            </mark>
        );
        matchIdx++;
        pos = idx + len;
    }

    return <>{parts}</>;
}

function countMatches(text: string, query: string): number {
    if (!query) return 0;
    let count = 0;
    let pos = 0;
    const lower = text.toLowerCase();
    const lowerQ = query.toLowerCase();
    while ((pos = lower.indexOf(lowerQ, pos)) !== -1) {
        count++;
        pos += lowerQ.length;
    }
    return count;
}

export function LatexPanel({ latex, latexFull, onImport }: LatexPanelProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editedLatex, setEditedLatex] = useState(latex);
    const [showFull, setShowFull] = useState(true);
    const [isCompiling, setIsCompiling] = useState(false);

    // Search state
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [matchIndex, setMatchIndex] = useState(0);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const preRef = useRef<HTMLPreElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const savedScrollRef = useRef(0);

    // Drag-to-resize state
    const [panelHeight, setPanelHeight] = useState(() => {
        const saved = localStorage.getItem("seetex-latex-panel-height");
        return saved ? Number(saved) : window.innerHeight * 0.4;
    });
    const dragStartRef = useRef<{ y: number; height: number } | null>(null);

    const displayLatex = showFull ? latexFull : latex;
    const totalMatches = countMatches(displayLatex, searchQuery);

    // Persist panel height
    useEffect(() => {
        localStorage.setItem("seetex-latex-panel-height", String(panelHeight));
    }, [panelHeight]);

    // Drag-to-resize handlers
    const onResizePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        dragStartRef.current = { y: e.clientY, height: panelHeight };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }, [panelHeight]);

    const onResizePointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
        if (!dragStartRef.current) return;
        const delta = dragStartRef.current.y - e.clientY;
        const newHeight = Math.max(120, Math.min(window.innerHeight * 0.85, dragStartRef.current.height + delta));
        setPanelHeight(newHeight);
    }, []);

    const onResizePointerUp = useCallback(() => {
        dragStartRef.current = null;
    }, []);

    // Clamp matchIndex when count changes
    useEffect(() => {
        if (totalMatches === 0) setMatchIndex(0);
        else setMatchIndex(i => Math.min(i, totalMatches - 1));
    }, [totalMatches]);

    // Scroll the current match into view
    useEffect(() => {
        if (!preRef.current || !searchQuery) return;
        const el = preRef.current.querySelector<HTMLElement>(".latex-match--current");
        el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, [matchIndex, searchQuery]);

    const openSearch = useCallback(() => {
        setShowSearch(true);
        setTimeout(() => searchInputRef.current?.focus(), 0);
    }, []);

    const closeSearch = useCallback(() => {
        setShowSearch(false);
        setSearchQuery("");
        setMatchIndex(0);
    }, []);

    const nextMatch = useCallback(() => {
        if (totalMatches === 0) return;
        setMatchIndex(i => (i + 1) % totalMatches);
    }, [totalMatches]);

    const prevMatch = useCallback(() => {
        if (totalMatches === 0) return;
        setMatchIndex(i => (i - 1 + totalMatches) % totalMatches);
    }, [totalMatches]);

    // Cmd/Ctrl+F opens search
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "f") {
                e.preventDefault();
                openSearch();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [isOpen, openSearch]);

    const handleImport = () => {
        if (onImport && editedLatex !== latex) {
            onImport(editedLatex);
            setEditMode(false);
        }
    };

    const handleCancel = () => {
        savedScrollRef.current = bodyRef.current?.scrollTop ?? 0;
        setEditedLatex(showFull ? latexFull : latex);
        setEditMode(false);
    };

    const handleToggleView = () => {
        const newShowFull = !showFull;
        setShowFull(newShowFull);
        if (editMode) {
            setEditedLatex(newShowFull ? latexFull : latex);
        }
    };

    const handleExportPdf = async () => {
        setIsCompiling(true);
        try {
            const titleMatch = latexFull.match(/\\title\{([^}]*)\}/);
            const documentName = titleMatch ? titleMatch[1] : undefined;
            await invoke('compile_to_pdf', { latexContent: latexFull, documentName });
        } catch (err) {
            alert(`PDF compilation failed: ${err}`);
        } finally {
            setIsCompiling(false);
        }
    };

    return (
        <>
            {/* Toggle button */}
            <button
                className="latex-panel-toggle"
                onClick={() => setIsOpen(!isOpen)}
                title={isOpen ? "Hide LaTeX" : "Show LaTeX"}
            >
                {isOpen ? "⌃ Hide LaTeX" : "⌄ Show LaTeX"}
            </button>

            {/* Panel */}
            {isOpen && (
                <div className="latex-panel" style={{ height: panelHeight }}>
                    {/* Drag handle */}
                    <div
                        className="latex-panel-resize-handle"
                        onPointerDown={onResizePointerDown}
                        onPointerMove={onResizePointerMove}
                        onPointerUp={onResizePointerUp}
                    />
                    <div className="latex-panel-header">
                        <span className="latex-panel-title">LaTeX Source</span>
                        <div className="latex-panel-actions">
                            {!editMode && (
                                <>
                                    <button
                                        className="latex-panel-btn latex-panel-btn--secondary"
                                        onClick={handleToggleView}
                                    >
                                        {showFull ? "Body Only" : "Full Document"}
                                    </button>
                                    <button
                                        className="latex-panel-btn latex-panel-btn--secondary"
                                        onClick={openSearch}
                                        title="Search (Cmd+F)"
                                    >
                                        Search
                                    </button>
                                </>
                            )}
                            {!editMode ? (
                                <>
                                    <button
                                        className="latex-panel-btn latex-panel-btn--secondary"
                                        onClick={() => { navigator.clipboard.writeText(displayLatex); }}
                                    >
                                        Copy
                                    </button>
                                    <button
                                        className="latex-panel-btn latex-panel-btn--secondary"
                                        onClick={() => {
                                            savedScrollRef.current = bodyRef.current?.scrollTop ?? 0;
                                            setEditedLatex(displayLatex);
                                            setEditMode(true);
                                        }}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        className="latex-panel-btn latex-panel-btn--primary"
                                        onClick={handleExportPdf}
                                        disabled={isCompiling}
                                    >
                                        {isCompiling ? "Compiling..." : "Get PDF"}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        className="latex-panel-btn latex-panel-btn--secondary"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="latex-panel-btn latex-panel-btn--primary"
                                        onClick={handleImport}
                                    >
                                        Import
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Search bar */}
                    {showSearch && !editMode && (
                        <div className="latex-search-bar">
                            <input
                                ref={searchInputRef}
                                className="latex-search-input"
                                placeholder="Search LaTeX…"
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); setMatchIndex(0); }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) nextMatch();
                                    else if (e.key === "Enter" && e.shiftKey) prevMatch();
                                    else if (e.key === "Escape") closeSearch();
                                }}
                                spellCheck={false}
                            />
                            <span className="latex-search-count">
                                {searchQuery
                                    ? totalMatches === 0
                                        ? "No results"
                                        : `${matchIndex + 1} / ${totalMatches}`
                                    : ""}
                            </span>
                            <button
                                className="latex-search-nav"
                                onClick={prevMatch}
                                disabled={totalMatches === 0}
                                title="Previous (Shift+Enter)"
                            >↑</button>
                            <button
                                className="latex-search-nav"
                                onClick={nextMatch}
                                disabled={totalMatches === 0}
                                title="Next (Enter)"
                            >↓</button>
                            <button
                                className="latex-search-close"
                                onClick={closeSearch}
                                title="Close (Esc)"
                            >✕</button>
                        </div>
                    )}

                    <div ref={bodyRef} className="latex-panel-body">
                        {editMode ? (
                            <textarea
                                className="latex-panel-textarea"
                                value={editedLatex}
                                onChange={(e) => setEditedLatex(e.target.value)}
                                spellCheck={false}
                                ref={(el) => {
                                    if (el && bodyRef.current) {
                                        const body = bodyRef.current;
                                        const scroll = savedScrollRef.current;
                                        requestAnimationFrame(() => {
                                            body.scrollTop = scroll;
                                        });
                                    }
                                }}
                            />
                        ) : (
                            <pre ref={preRef} className="latex-panel-pre">
                                <code>
                                    <HighlightedCode
                                        text={displayLatex}
                                        query={searchQuery}
                                        currentMatch={matchIndex}
                                    />
                                </code>
                            </pre>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
